import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Skeleton,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { fetchCustomers, updateCustomer } from "../../api/customers";
import { Context, useCurrentProfile } from "../../ContextWrapper";
import CustomTableContainer from "../../theme/components/tableContainer";
import { SearchIcon } from "@chakra-ui/icons";
import { fetchGoogleDriveFolders } from "../../api/google";
import { GenericModal } from "../generic/modal";
import { DropDown } from "../generic/dropDown";
import { GoogleDriveView } from "../generic/googleDriveView";

export const GoogleFolderLink = () => {
  const [customerList, setCustomerList] = useState([]);
  const [folders, setFolders] = useState([]);
  const [search, setSearch] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursor, setPreviousCursor] = useState(null);
  const context = React.useContext(Context);
  const { profile } = useCurrentProfile();
  const [hideLinked, setHideLinked] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isTestOpen,
    onOpen: onTestOpen,
    onClose: onTestClose,
  } = useDisclosure();

  const refreshCustomersList = useCallback(
    (cursor) => {
      let params = {};
      if (cursor) {
        const urlObj = new URL(cursor);
        params.cursor = urlObj.searchParams.get("cursor");
      }

      if (search) {
        params.search = search;
      }

      if (hideLinked) {
        params.hide_linked_google_drive = true;
      }

      const fetchData = async () => {
        const data = await fetchCustomers(profile, params);
        setCustomerList(data.results);
        setNextCursor(data.next);
        setPreviousCursor(data.previous);

        const folderData = await fetchGoogleDriveFolders();
        setFolders(folderData);
        setLoading(false);
      };
      fetchData();
    },
    [profile, search, hideLinked]
  );

  useEffect(() => {
    refreshCustomersList();
  }, [profile, refreshCustomersList]);

  const handleNextPage = () => {
    if (nextCursor) {
      refreshCustomersList(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    if (previousCursor) {
      refreshCustomersList(previousCursor);
    }
  };

  const getFolderName = (folderId) => {
    if (!folderId) {
      return null;
    }
    return folders.find((f) => f.value === folderId)?.label;
  };

  const openLinkModal = (customer) => {
    setSelectedCustomer(customer);
    onOpen();
  };

  const openTestModal = (customer) => {
    setSelectedCustomer(customer);
    onTestOpen();
  };

  const handleSubmitLink = (event, folder) => {
    event.preventDefault();

    updateCustomer(selectedCustomer.id, { google_drive_folder_id: folder })
      .then((res) => {
        if (!hideLinked) {
          setCustomerList((prevList) =>
            prevList.map((c) => (c.id === res.data.id ? res.data : c))
          );
        } else {
          setCustomerList((prevList) =>
            prevList.filter((c) => c.id !== res.data.id)
          );
        }
        onClose();
        context.fireToast("Success", "Account updated.", "success");
      })
      .catch((err) => console.log(err));
  };

  return (
    <Container maxW="100%" bg="brand.50" h="calc(91vh)">
      <Stack direction="column" spacing={2} align="center">
        <CustomTableContainer width="90%">
          <Flex pb="5">
            <Box>
              <Heading as="h3" size="lg">
                Accounts
              </Heading>
            </Box>
            <Spacer />
            <Box>
              <Stack direction="row" spacing={6} align="center">
                <LinkModal
                  isOpen={isOpen}
                  onClose={onClose}
                  customer={selectedCustomer}
                  folders={folders}
                  handleSubmit={handleSubmitLink}
                />
                <TestModal
                  isOpen={isTestOpen}
                  onClose={onTestClose}
                  customer={selectedCustomer}
                />
                <Checkbox
                  isChecked={hideLinked}
                  onChange={() => setHideLinked(!hideLinked)}
                >
                  Hide linked
                </Checkbox>
                <InputGroup w="340px">
                  <Input
                    placeholder="Search..."
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <InputRightElement
                    bg="brand.500"
                    borderBottomRightRadius={6}
                    borderTopRightRadius={6}
                  >
                    <SearchIcon variant="white" />
                  </InputRightElement>
                </InputGroup>
              </Stack>
            </Box>
          </Flex>
          <Box maxHeight="calc(100vh - 300px)" bg="white" overflowX="auto">
            <Table variant="unstyled" width="100%">
              <Thead position="sticky" top={-1} zIndex={1}>
                <Tr>
                  <Th>Business Name</Th>
                  <Th>Google Drive Folder</Th>
                  <Th style={{ width: "118px" }}>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {customerList.map((customer) => (
                  <Tr key={customer.id}>
                    <Td style={{ paddingRight: "0px" }}>{customer.name}</Td>
                    <Td>
                      {loading ? (
                        <Skeleton height="20px" width="300px" />
                      ) : (
                        getFolderName(customer.google_drive_folder_id)
                      )}
                    </Td>
                    <Td pt="1" pb="1">
                      <Stack p="0" direction="row" spacing={2} align="center">
                        <Button
                          variant="outline"
                          onClick={() => openLinkModal(customer)}
                        >
                          Link
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => openTestModal(customer)}
                        >
                          Test
                        </Button>
                      </Stack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <Stack direction="row" spacing={4} mt={4} justifyContent="center">
            {previousCursor && (
              <Button onClick={handlePreviousPage}>Previous</Button>
            )}
            {nextCursor && (
              <Button onClick={handleNextPage} disabled={!nextCursor}>
                Next
              </Button>
            )}
          </Stack>
        </CustomTableContainer>
      </Stack>
    </Container>
  );
};

const LinkModal = ({ isOpen, onClose, customer, folders, handleSubmit }) => {
  const [selectedFolder, setSelectedFolder] = useState("");

  useEffect(() => {
    setSelectedFolder(customer?.google_drive_folder_id);
  }, [customer]);

  const handleSelectFolder = (folder) => {
    setSelectedFolder(folder.value);
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Link Google Drive Folder`}
      submitButton={
        <Button type="submit" form="linkForm" ml={3}>
          Link
        </Button>
      }
      body={
        <>
          <form
            id={"linkForm"}
            onSubmit={(e) => handleSubmit(e, selectedFolder)}
          >
            <FormControl>
              <FormLabel htmlFor="customer">Add Account</FormLabel>
              <DropDown
                name="folder"
                options={folders}
                selectedValue={selectedFolder}
                onChange={handleSelectFolder}
                isRequired={true}
              />
            </FormControl>
          </form>
        </>
      }
    ></GenericModal>
  );
};

const TestModal = ({ isOpen, onClose, customer }) => {
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Test Drive Folder - ${customer?.name}`}
      body={<GoogleDriveView customer={customer} />}
    ></GenericModal>
  );
};
